/**
 * Payroll without claims — Shayna, review #230 (2026-10-08): "a page that lists the
 * people where payroll is included in overall for CPC but doesn't have claims", with
 * each month and the dollars, for billers who bill some months and not others.
 *
 * One row per person with a paid month in the range that billed no claims. Each month
 * column is that month's claim labor when it billed none, blank when it billed claims
 * (a faint dot) or the person was not paid (empty). The figures are
 * `measures/payrollWithoutClaims.ts`; they are each person's unattributed labor month
 * by month, so the total is the Overview's unattributed labor for the same filters.
 *
 * Group by (review #230 follow-up, 2026-10-09): None, home department, or direct
 * supervisor — a collapsible header row per group with its month totals and people.
 * Why: one reason per claimless month and per person (measures/noClaimReason.ts), with
 * the dollars per reason as chips that also filter the table to that reason's months.
 *
 * Payroll by person: the rows come off `scope.people`, which is the scoped bundle
 * /api/dataset returned (api/shared/scope.ts) — a scoped user sees only their grant.
 * The reporting lines the Why column reads come off `derived.people`, the same response.
 */
import { useMemo, useState } from 'react'
import { useAppData } from '@/data/AppData'
import type { Person } from '@/data/selectors'
import {
  groupKey,
  groupRows,
  payrollWithoutClaims,
  type GroupBy,
  type NoClaimGroup,
  type NoClaimPerson,
} from '@/measures/payrollWithoutClaims'
import {
  explainRows,
  onlyReason,
  REASON,
  REASONS,
  reasonTotals,
  shownWhy,
  type PersonWhy,
  type ReasonKey,
} from '@/measures/noClaimReason'
import { DataTable, type Column } from '@/components/Table'
import { Select, StatStrip, Stat, TerminatedChip } from '@/components/Bits'
import { ExportBar } from '@/components/ExportBar'
import { PopOut } from '@/components/PopOut'
import { MODELED_FILTER_NOTE } from '@/components/MultiSelect'
import { useUrlState, oneOf } from '@/ui/urlState'
import { dateUs, int, money0, moneyK, monthSpan } from '@/format'

/** "Jan", or "Jan 26" when the range crosses a year. */
const monthHeader = (m: string, years: boolean) =>
  new Date(`${m}-01T00:00:00`).toLocaleString('en-US', {
    month: 'short',
    ...(years ? { year: '2-digit' } : {}),
  })

/** Title · department · reports to — the line under the name. */
const subLine = (p: Person) =>
  [p.jobTitle, p.department, p.supervisor ? `reports to ${p.supervisor}` : null]
    .filter(Boolean)
    .join(' · ')

export const WHY_IN_COST_PER_CLAIM =
  'This labor is in cost per claim because the org’s cost per claim divides all claim labor paid to RCM staff by billed claims, whether or not the labor landed on a claim — so a month of pay with no claims behind it raises the cost of every claim.'

/** A person's row, or a group's header row (`DataTable` groups). */
type Line = { kind: 'person'; r: NoClaimPerson } | { kind: 'group'; g: NoClaimGroup }

const GROUP_BYS = ['none', 'department', 'supervisor'] as const
const GROUP_LABEL: Record<GroupBy, string> = { none: 'None', department: 'Department', supervisor: 'Supervisor' }
const GROUP_OPTION: Record<GroupBy, string> = {
  none: 'None',
  department: 'Department (home)',
  supervisor: 'Supervisor (direct)',
}
const REASON_FILTER = [...REASONS.map((r) => r.key), 'all'] as const

/** The reason's colour dot. Never the only cue: its label is beside it or on hover. */
function WhyDot({ reason }: { reason: ReasonKey }) {
  return <span className={`why-dot why-${reason}`} aria-hidden="true" />
}

/** "Mar, Apr: New hire" — the months whose reason differs from the person's. */
function otherMonths(why: PersonWhy, years: boolean): string | null {
  const by = new Map<ReasonKey, string[]>()
  for (const [m, k] of [...why.byMonth].sort(([a], [b]) => a.localeCompare(b))) {
    if (k === why.reason) continue
    by.set(k, [...(by.get(k) ?? []), monthHeader(m, years)])
  }
  if (by.size === 0) return null
  return [...by].map(([k, ms]) => `${ms.join(', ')}: ${REASON[k].label}`).join('; ')
}

export function PayrollWithoutClaims() {
  const { derived, scope, openPerson } = useAppData()
  const months = derived.months
  const years = new Set(months.map((m) => m.slice(0, 4))).size > 1
  const [groupBy, setGroupBy] = useUrlState<GroupBy>('group', 'none', oneOf(GROUP_BYS, 'none'))
  const [reason, setReason] = useUrlState<ReasonKey | 'all'>('why', 'all', oneOf(REASON_FILTER, 'all'))
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())

  const all = useMemo(() => payrollWithoutClaims(scope.people), [scope.people])
  // Who has someone reporting to them, over everyone in the response — a supervisor
  // whose reports billed every month is still a supervisor.
  const reporting = useMemo(
    () => new Set(derived.people.map((p) => p.supervisor).filter((s): s is string => s !== null)),
    [derived.people],
  )
  const why = useMemo(() => explainRows(all.rows, reporting), [all.rows, reporting])
  const totals = useMemo(() => reasonTotals(all.rows, why), [all.rows, why])
  const { rows, byMonth, total } = useMemo(
    () => (reason === 'all' ? all : onlyReason(all, why, reason)),
    [all, why, reason],
  )
  const original = useMemo(() => new Map(all.rows.map((r) => [r.person.employee, r])), [all.rows])
  const personMonths = rows.reduce((a, r) => a + r.withoutClaims.size, 0)
  const mixed = rows.filter((r) => r.withClaims.size > 0).length

  const groups = useMemo(
    () => (groupBy === 'none' ? null : new Map(groupRows(rows, groupBy).map((g) => [g.key, g]))),
    [rows, groupBy],
  )
  const lines = useMemo((): Line[] => rows.map((r) => ({ kind: 'person', r })), [rows])

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // Under a reason filter a row shows that reason, not the person's overall one (#393).
  const whyOf = (r: NoClaimPerson) => {
    const w = why.get(r.person.employee)
    return w && shownWhy(w, reason)
  }

  const columns: Column<Line>[] = [
    {
      key: 'person',
      header: 'Person',
      frozen: true,
      width: 260,
      sort: (l) => (l.kind === 'group' ? l.g.key : l.r.person.employee),
      render: (l) => {
        if (l.kind === 'group')
          return (
            <>
              <span className="grp-caret" aria-hidden="true">
                {collapsed.has(l.g.key) ? '▸' : '▾'}
              </span>
              <b>{l.g.key}</b>
              <br />
              <span className="sub2">
                {int(l.g.rows.length)} {l.g.rows.length === 1 ? 'person' : 'people'}
              </span>
            </>
          )
        const r = l.r
        return (
          <>
            <span className="nm">{r.person.employee}</span>
            {r.person.terminated && (
              <>
                {' '}
                <TerminatedChip date={r.person.terminationDate} />
              </>
            )}
            {r.person.management && (
              <>
                {' '}
                <span className="pts-chip neut">mgmt</span>
              </>
            )}
            <br />
            <span className="sub2">{subLine(r.person)}</span>
          </>
        )
      },
    },
    {
      key: 'why',
      header: 'Why',
      width: 210,
      title:
        'Why this person was paid in months they billed no claims: the reason carrying most of their dollars, or under a reason filter that reason. Hover it for the rule; Data & method has them all.',
      sort: (l) => (l.kind === 'group' ? null : REASON[whyOf(l.r)?.reason ?? 'unknown'].label),
      render: (l) => {
        if (l.kind === 'group') return null
        const w = whyOf(l.r)
        if (!w) return null
        const r = REASON[w.reason]
        const other = otherMonths(w, years)
        // The date the reason reads, beside it (review #605).
        const left = r.key === 'left' && l.r.person.terminationDate
        return (
          <span title={r.explain} data-reason={r.key}>
            <WhyDot reason={r.key} /> {r.label}
            {left && (
              <>
                <br />
                <span className="sub2">Terminated {dateUs(l.r.person.terminationDate)}</span>
              </>
            )}
            {other && (
              <>
                <br />
                <span className="sub2">{other}</span>
              </>
            )}
          </span>
        )
      },
    },
    ...months.map(
      (m, i): Column<Line> => ({
        key: `m${m}`,
        header: monthHeader(m, years),
        align: 'right',
        width: 92,
        divider: i === 0,
        title: `${monthSpan([m])}: claim labor in a month that billed no claims`,
        sort: (l) => (l.kind === 'group' ? (l.g.byMonth.get(m) ?? null) : (l.r.withoutClaims.get(m) ?? null)),
        render: (l) => {
          if (l.kind === 'group') {
            const v = l.g.byMonth.get(m)
            return v === undefined ? null : <b className="num">{money0(v)}</b>
          }
          const r = l.r
          const w = whyOf(r)
          const v = r.withoutClaims.get(m)
          if (v !== undefined) {
            const k = w?.byMonth.get(m)
            // A month whose reason differs from the person's carries its own dot.
            return k && k !== w?.reason ? (
              <span className="num" title={`${REASON[k].label}: ${REASON[k].explain}`} data-reason={k}>
                <WhyDot reason={k} /> {money0(v)}
              </span>
            ) : (
              <span className="num">{money0(v)}</span>
            )
          }
          // Under a reason filter: a claimless month with another reason.
          if (original.get(r.person.employee)?.withoutClaims.has(m)) {
            const k = w?.byMonth.get(m) ?? 'unknown'
            return (
              <span style={{ color: 'var(--ink-3)' }} title={`Another reason this month: ${REASON[k].label}`}>
                –
              </span>
            )
          }
          if (r.withClaims.has(m))
            return (
              <span style={{ color: 'var(--ink-3)' }} title="Billed claims this month — its labor sits on claims">
                ·
              </span>
            )
          return <span title="Not paid this month" />
        },
      }),
    ),
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      width: 104,
      divider: true,
      sort: (l) => (l.kind === 'group' ? l.g.total : l.r.total),
      render: (l) => <b className="num">{money0(l.kind === 'group' ? l.g.total : l.r.total)}</b>,
    },
    {
      key: 'without',
      header: 'Months without claims',
      align: 'right',
      width: 96,
      sort: (l) => (l.kind === 'group' ? l.g.monthsWithout : l.r.withoutClaims.size),
      render: (l) => (
        <span className="num">{int(l.kind === 'group' ? l.g.monthsWithout : l.r.withoutClaims.size)}</span>
      ),
    },
    {
      key: 'with',
      header: 'Months with claims',
      align: 'right',
      width: 96,
      sort: (l) => (l.kind === 'group' ? l.g.monthsWith : l.r.withClaims.size),
      render: (l) => <span className="num">{int(l.kind === 'group' ? l.g.monthsWith : l.r.withClaims.size)}</span>,
    },
  ]

  return (
    <>
      <h1 className="page-title">Payroll without claims</h1>
      <p className="body-note" style={{ maxWidth: 760, marginTop: 6 }} data-testid="why">
        People paid in a month they billed no claims, and what that month cost. {WHY_IN_COST_PER_CLAIM}
      </p>

      <div style={{ marginTop: 16 }}>
        <StatStrip cols={4}>
          <Stat
            label="Payroll without claims"
            value={money0(total)}
            note={`claim labor, ${monthSpan(months)}${reason === 'all' ? '' : ` — ${REASON[reason].label} only`}`}
            modeled={scope.modeled}
            hero
          />
          <Stat label="People" value={int(rows.length)} note={`${int(mixed)} of them billed claims in other months`} />
          <Stat label="Months without claims" value={int(personMonths)} note="person-months" />
        </StatStrip>
      </div>

      {totals.size > 0 && (
        <div
          className="why-chips"
          style={{ marginTop: 14 }}
          data-testid="why-totals"
          role="group"
          aria-label="Payroll without claims by reason"
        >
          <span className="why-chips-lead">By reason</span>
          {REASONS.filter((r) => totals.has(r.key)).map((r) => {
            const t = totals.get(r.key)!
            const on = reason === r.key
            return (
              <button
                key={r.key}
                type="button"
                className={`why-chip${on ? ' on' : ''}`}
                aria-pressed={on}
                data-reason={r.key}
                title={`${r.explain} ${int(t.people.size)} ${t.people.size === 1 ? 'person' : 'people'}. ${on ? 'Click again for every reason.' : 'Click to show only these months.'}`}
                onClick={() => setReason(on ? 'all' : r.key)}
              >
                <WhyDot reason={r.key} /> {r.plural} <b className="num">{moneyK(t.total)}</b>
              </button>
            )
          })}
          {reason !== 'all' && (
            <button type="button" className="why-chip clear" onClick={() => setReason('all')}>
              Every reason
            </button>
          )}
        </div>
      )}

      <PopOut title="Payroll without claims, by person and month" style={{ marginTop: 16 }}>
        {({ button, enlarged }) => (
          <div className="card popout-fill">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '8px 12px' }}>
              {!enlarged && (
                <h3 className="card-heading" style={{ marginRight: 'auto' }}>
                  {groupBy === 'none'
                    ? 'By person and month'
                    : `By ${GROUP_LABEL[groupBy].toLowerCase()}, person and month`}
                  {reason !== 'all' && ` — ${REASON[reason].label}`}
                </h3>
              )}
              <div style={{ minWidth: 170 }}>
                <Select
                  label="Group by"
                  value={groupBy}
                  options={GROUP_BYS.map((g) => ({ value: g, label: GROUP_OPTION[g] }))}
                  onChange={(v) => {
                    setGroupBy(v as GroupBy)
                    setCollapsed(new Set())
                  }}
                />
              </div>
              {groups && (
                <button
                  type="button"
                  className="why-chip clear"
                  onClick={() => setCollapsed(collapsed.size > 0 ? new Set() : new Set(groups.keys()))}
                >
                  {collapsed.size > 0 ? 'Expand all' : 'Collapse all'}
                </button>
              )}
            </div>
            <ExportBar
              filename="payroll-without-claims.csv"
              rows={rows}
              lead={button}
              columns={[
                ...(groupBy === 'none'
                  ? []
                  : [{ header: GROUP_LABEL[groupBy], value: (r: NoClaimPerson) => groupKey(r.person, groupBy) }]),
                { header: 'Employee', value: (r) => r.person.employee },
                { header: 'Job title', value: (r) => r.person.jobTitle },
                { header: 'Department', value: (r) => r.person.department },
                { header: 'Supervisor', value: (r) => r.person.supervisor },
                { header: 'Termination date', value: (r) => r.person.terminationDate ?? '' },
                { header: 'Why', value: (r) => REASON[whyOf(r)?.reason ?? 'unknown'].label },
                {
                  header: 'Why, other months',
                  value: (r) => {
                    const w = whyOf(r)
                    return w ? otherMonths(w, years) : null
                  },
                },
                ...months.map((m) => ({
                  header: monthSpan([m]),
                  value: (r: NoClaimPerson) => {
                    const v = r.withoutClaims.get(m)
                    return v === undefined ? null : Math.round(v * 100) / 100
                  },
                })),
                { header: 'Total', value: (r) => Math.round(r.total * 100) / 100 },
                { header: 'Months without claims', value: (r) => r.withoutClaims.size },
                { header: 'Months with claims', value: (r) => r.withClaims.size },
              ]}
            />
            <div className="card emp-dense" style={{ padding: 0, overflow: 'hidden' }}>
              <DataTable
                storageKey="payroll-without-claims"
                rows={lines}
                columns={columns}
                rowKey={(l) => (l.kind === 'group' ? `g:${l.g.key}` : l.r.person.employee)}
                onRowClick={(l) => {
                  if (l.kind === 'person') openPerson(l.r.person.employee)
                }}
                groups={
                  groups && groupBy !== 'none'
                    ? {
                        of: (l) => (l.kind === 'person' ? groupKey(l.r.person, groupBy) : l.g.key),
                        summary: (key): Line => ({ kind: 'group', g: groups.get(key)! }),
                        collapsed,
                        onToggle: toggle,
                      }
                    : undefined
                }
                initialSort="total"
                initialDir="desc"
                sortParam="sort"
                maxHeight={enlarged ? 'calc(100vh - 170px)' : 640}
                empty={
                  reason === 'all'
                    ? 'Everyone in this scope billed claims in every month they were paid.'
                    : `No month without claims is “${REASON[reason].label}” under these filters.`
                }
              />
            </div>
            <p className="body-note" style={{ marginTop: 10 }} data-testid="month-totals">
              Month totals:{' '}
              {months.map((m, i) => (
                <span key={m}>
                  {i > 0 && ' · '}
                  {monthHeader(m, years)} <span className="num">{money0(byMonth.get(m) ?? 0)}</span>
                </span>
              ))}
              . A dot is a month the person billed claims; an empty cell, a month they were not
              paid; a dash, under a reason filter, a month with another reason. A coloured mark
              beside a month’s dollars is a reason that differs from the person’s. The dollars are
              claim labor — benefit-loaded, as cost per claim counts it — not the pay on the
              person’s page.
              {scope.modeled && ` ${MODELED_FILTER_NOTE}`}
            </p>
          </div>
        )}
      </PopOut>
    </>
  )
}
